import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookOpen, Download, Search, Filter, Award, Star, Trophy, CheckCircle, X, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { getPillars, getThemes } from '../data/taxonomy';
import { API_BASE_URL } from '../config/api';

export default function Marketplace() {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPillar, setSelectedPillar] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [availableThemes, setAvailableThemes] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  const pillars = getPillars();

  useEffect(() => {
    if (selectedPillar) {
      setAvailableThemes(getThemes(selectedPillar));
      setSelectedTheme('');
    } else {
      setAvailableThemes([]);
      setSelectedTheme('');
    }
  }, [selectedPillar]);

  useEffect(() => {
    fetchPapers();
  }, [selectedPillar, selectedTheme, searchTerm]);

  const fetchPapers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedPillar) params.pillar = selectedPillar;
      if (selectedTheme) params.theme = selectedTheme;
      if (searchTerm) params.search = searchTerm;

      const { data } = await axios.get(
        `${API_BASE_URL}/api/papers/`,
        { params }
      );
      
      // Filter papers based on user role
      let filteredPapers = data;
      if (user && user.role === 'admin') {
        filteredPapers = data;
      } else {
        filteredPapers = data.filter(paper => 
          paper.status === 'published' || paper.status === 'active'
        );
      }
      
      setPapers(filteredPapers);
    } catch (error) {
      console.error('Error fetching papers:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedPillar('');
    setSelectedTheme('');
  };

  const hasActiveFilters = searchTerm || selectedPillar || selectedTheme;
  const activeFilterCount = [selectedPillar, selectedTheme, searchTerm].filter(Boolean).length;

  const handlePaperClick = (paperId) => {
    navigate(`/research-library/paper/${paperId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8f] text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Research Library & Insights
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl">
            Evidence-driven analysis for informed development decisions — across Economic Stability, Social Equity, and Environmental Resilience
          </p>
          {user && (
            <p className="mt-4 text-sm">Welcome back, <span className="font-semibold">{user.name}</span></p>
          )}
        </div>
      </div>

      {/* Research & Insights Content */}
      <div className="container mx-auto px-4 pt-12">
        <div className="max-w-5xl mx-auto" data-testid="research-insights-content">
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            CSED's research platform is built on the conviction that meaningful development must be guided by <strong>evidence, not assumption</strong>. In a landscape where economic growth, social outcomes and environmental sustainability are often addressed in isolation, CSED produces and curates work that is both intellectually rigorous and practically relevant.
          </p>
          <div className="grid md:grid-cols-3 gap-4 my-8">
            {[['Economic Stability'], ['Social Equity'], ['Environmental Resilience']].map(([p]) => (
              <div key={p} className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-[#E87722]">
                <h3 className="font-bold text-[#1E3A5F]">{p}</h3>
                <p className="text-xs text-gray-500 mt-1">Core research pillar</p>
              </div>
            ))}
          </div>
          <div className="bg-white border rounded-xl p-6 mb-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h2 className="text-lg font-bold text-[#1E3A5F] mb-2">Curated Research Repository</h2>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Research papers, policy briefs, insight notes, case studies and position papers — each selected through a structured framework assessing <strong>relevance, analytical depth and practical applicability</strong>.
                </p>
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#1E3A5F] mb-2">From Insight to Action</h2>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Our research informs <strong>policy formulation</strong> at state and national levels, <strong>institutional strategy</strong> for development organizations, and <strong>capital deployment</strong> decisions by funders and investors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="container mx-auto px-4 py-8">
        <Card className="p-6 mb-8">
          {/* Search Bar */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              type="text"
              placeholder="Search by title, description, keywords, or authors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4"
            />
          </div>

          {/* Filter Toggle and Clear Button */}
          <div className="flex justify-between items-center mb-4">
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              {activeFilterCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-[#E87722] text-white text-xs rounded-full font-semibold">
                  {activeFilterCount}
                </span>
              )}
            </Button>

            {hasActiveFilters && (
              <Button
                onClick={clearFilters}
                variant="ghost"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 flex items-center gap-1"
              >
                <X className="h-4 w-4" />
                Clear All
              </Button>
            )}
          </div>

          {/* Collapsible Filters */}
          {showFilters && (
            <div className="grid md:grid-cols-4 gap-4 pt-4 border-t">
              {/* Filter by Pillar */}
              <div>
                <Label className="text-sm font-medium mb-2 block text-gray-700">Pillar</Label>
                <select
                  value={selectedPillar}
                  onChange={(e) => setSelectedPillar(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E87722] focus:border-transparent bg-white"
                >
                  <option value="">All Pillars</option>
                  {pillars.map((pillar) => (
                    <option key={pillar} value={pillar}>
                      {pillar}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filter by Theme */}
              <div>
                <Label className="text-sm font-medium mb-2 block text-gray-700">Theme</Label>
                <select
                  value={selectedTheme}
                  onChange={(e) => setSelectedTheme(e.target.value)}
                  disabled={!selectedPillar}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E87722] focus:border-transparent disabled:bg-gray-100 bg-white"
                >
                  <option value="">All Themes</option>
                  {availableThemes.map((theme) => (
                    <option key={theme} value={theme}>
                      {theme}
                    </option>
                  ))}
                </select>
                {!selectedPillar && (
                  <p className="text-xs text-gray-500 mt-1">Select a pillar first</p>
                )}
              </div>
            </div>
          )}

          {/* Active Filter Badges */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
              <span className="text-sm text-gray-600 font-medium">Active filters:</span>
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                  Search: "{searchTerm.substring(0, 20)}{searchTerm.length > 20 ? '...' : ''}"
                  <button onClick={() => setSearchTerm('')} className="hover:text-blue-900 ml-1">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {selectedPillar && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                  Pillar: {selectedPillar}
                  <button onClick={() => setSelectedPillar('')} className="hover:text-green-900 ml-1">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {selectedTheme && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                  Theme: {selectedTheme}
                  <button onClick={() => setSelectedTheme('')} className="hover:text-purple-900 ml-1">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </Card>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-gray-600">
            {loading ? 'Loading...' : `Found ${papers.length} research paper${papers.length !== 1 ? 's' : ''}`}
            {hasActiveFilters && ' matching your filters'}
          </p>
        </div>

        {/* Papers Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87722]"></div>
            <p className="mt-4 text-gray-600">Loading research papers...</p>
          </div>
        ) : papers.length === 0 ? (
          <Card className="p-12 text-center">
            <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No papers found</h3>
            <p className="text-gray-600 mb-4">
              {hasActiveFilters
                ? 'Try adjusting your filters to see more results'
                : 'No research papers available at the moment'}
            </p>
            {hasActiveFilters && (
              <Button onClick={clearFilters} className="bg-[#E87722] hover:bg-[#d66a1a] text-white">
                Clear Filters
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {papers.map((paper) => (
              <Card
                key={paper.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handlePaperClick(paper.id)}
              >
                <div className="p-6">
                  {/* Badges Row */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="inline-block px-3 py-1 bg-[#1E3A5F] text-white text-xs rounded-full">
                      {paper.pillar}
                    </span>
                    
                    {paper.rating_band && (
                      <>
                        {paper.rating_band === 'flagship' && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                            <Trophy className="h-3 w-3" />
                            Flagship
                          </span>
                        )}
                        {paper.rating_band === 'strong' && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                            <Star className="h-3 w-3" />
                            Strong
                          </span>
                        )}
                        {paper.rating_band === 'moderate' && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                            <CheckCircle className="h-3 w-3" />
                            Moderate
                          </span>
                        )}
                      </>
                    )}
                    
                    {paper.expert_reviewed && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded-full">
                        <Award className="h-3 w-3" />
                        Expert Reviewed
                      </span>
                    )}
                    
                    {user && user.role === 'admin' && paper.status && (
                      <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                        paper.status === 'published' ? 'bg-green-100 text-green-800' :
                        paper.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                        paper.status === 'under_review' ? 'bg-yellow-100 text-yellow-800' :
                        paper.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {paper.status.replace('_', ' ').toUpperCase()}
                      </span>
                    )}
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                    {paper.title}
                  </h3>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {paper.description}
                  </p>

                  {paper.total_score !== undefined && (
                    <div className="mb-2">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500">Quality Score:</span>
                        <span className="font-bold text-[#E87722]">{paper.total_score}/30</span>
                      </div>
                    </div>
                  )}

                  <p className="text-sm text-gray-500 mb-2">
                    <span className="font-semibold">Theme:</span> {paper.theme}
                  </p>

                  {paper.authors && paper.authors.length > 0 && (
                    <p className="text-sm text-gray-500 mb-4">
                      <span className="font-semibold">Authors:</span> {paper.authors.join(', ')}
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex items-center gap-1 text-[#1E3A5F] font-semibold text-sm bg-[#1E3A5F]/5 px-3 py-1 rounded-full">
                      <Lock className="h-3.5 w-3.5" />
                      <span>Members Only</span>
                    </div>
                    <div className="flex items-center text-gray-500 text-sm">
                      <Download className="h-4 w-4 mr-1" />
                      <span>{paper.downloads} downloads</span>
                    </div>
                  </div>

                  <Button 
                    className="w-full mt-4 bg-[#E87722] hover:bg-[#d66a1a] text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePaperClick(paper.id);
                    }}
                  >
                    View Details
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
